import { UniqueIdentifier } from "@dnd-kit/core";
import { useCard } from "@/hooks/store/useCard";
import { useCallback, useMemo } from "react";
import { HexColor, Timer } from "@/types/v2";
import { card as cardRepository } from "@/repositories/cards";
import { useStore } from "@/stores/useStore";
import { DEFAULT_CARD_TIMER_UNITS, DEFAULT_TIMER_VALUE } from "@/lib/const";
export const useCardActions = (cardId: string | UniqueIdentifier) => {
  const { shopId, shop } = useStore();

  const defaultTimer = useMemo(
    () => shop?.defaultTimer ?? DEFAULT_TIMER_VALUE,
    [shop?.defaultTimer]
  );
  const timeDisplayUnits = useMemo(
    () => shop?.cardConfig.timerUnits ?? DEFAULT_CARD_TIMER_UNITS,
    [shop?.cardConfig.timerUnits]
  );

  const { card, setCard } = useCard(cardId);

  const handleColorChange = useCallback(
    (color: HexColor) => {
      if (!shopId || !cardId) return;
      const update = cardRepository.changeCardColor(
        shopId,
        cardId as string,
        color
      );
      return setCard((prevState) =>
        prevState
          ? {
              ...prevState,
              ...update,
            }
          : undefined
      );
    },
    [cardId, setCard, shopId]
  );

  const getCurrentTimer = useCallback((): Timer => {
    return (
      card?.timer ?? {
        timeStarted: null,
        timeLimitInMinutes: defaultTimer,
        accumulatedTime: 0,
        timeDisplayUnits: timeDisplayUnits,
      }
    );
  }, [card?.timer, defaultTimer, timeDisplayUnits]);

  const handleTimerLimitChange = useCallback(
    (timeLimit: string) => {
      if (!shopId || !cardId) return;
      const update = cardRepository.changeTimerLimit(
        shopId,
        cardId as string,
        timeLimit,
        getCurrentTimer()
      );
      return setCard((prevState) =>
        prevState
          ? {
              ...prevState,
              ...update,
            }
          : undefined
      );
    },
    [cardId, getCurrentTimer, setCard, shopId]
  );

  const handleTimerPause = useCallback(
    (accumulatedTime: number) => {
      if (!shopId || !cardId) return;
      const update = cardRepository.pauseTimer(
        shopId,
        cardId as string,
        accumulatedTime,
        getCurrentTimer()
      );
      return setCard((prevState) =>
        prevState
          ? {
              ...prevState,
              ...update,
            }
          : undefined
      );
    },
    [cardId, getCurrentTimer, setCard, shopId]
  );

  const handleTimerStart = useCallback(() => {
    if (!shopId || !cardId) return;
    const update = cardRepository.startTimer(
      shopId,
      cardId as string,
      getCurrentTimer()
    );
    return setCard((prevState) =>
      prevState
        ? {
            ...prevState,
            ...update,
          }
        : undefined
    );
  }, [cardId, getCurrentTimer, setCard, shopId]);

  const handleTimerStop = useCallback(() => {
    if (!shopId || !cardId) return;
    const update = cardRepository.stopTimer(
      shopId,
      cardId as string,
      getCurrentTimer()
    );
    return setCard((prevState) =>
      prevState
        ? {
            ...prevState,
            ...update,
          }
        : undefined
    );
  }, [cardId, getCurrentTimer, setCard, shopId]);

  const handleTimerReset = useCallback(() => {
    return handleTimerStop();
  }, [handleTimerStop]);

  const handleToggleStarred = useCallback(
    (isStarred: boolean) => {
      if (!shopId || !cardId) return;
      const update = cardRepository.toggleStarred(
        shopId,
        cardId as string,
        isStarred
      );
      return setCard((prevState) =>
        prevState
          ? {
              ...prevState,
              ...update,
            }
          : undefined
      );
    },
    [cardId, setCard, shopId]
  );

  return {
    handleColorChange,
    handleTimerLimitChange,
    handleTimerStart,
    handleTimerPause,
    handleTimerStop,
    handleTimerReset,
    handleToggleStarred,
    card,
  };
};
